import { useFormik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import {
  OfferFormValues, TopLevelField, deriveFieldRejection, emptyOfferPosition, findRejectedPositionIndex,
  offerFormSchema, positionFieldError, topLevelFieldError, validatePopupImageFile,
} from './offerFormLogic';

// One key per position, generated at addKey (a row is added) or by the effect (a new offer
// replaces the whole list) — never mutated during render. A same-length swap to a different
// offer reusing keys is harmless — keys only need to be stable for reconciliation, not unique.
function useStablePositionKeys(initialLength: number, resetToken: unknown) {
  const nextKeyRef = useRef(0);
  const makeKey = () => {
    nextKeyRef.current += 1;
    return nextKeyRef.current;
  };
  const [keys, setKeys] = useState<number[]>(() => Array.from({ length: initialLength }, makeKey));

  useEffect(() => {
    setKeys(Array.from({ length: initialLength }, makeKey));
  }, [resetToken, initialLength]);

  const addKey = () => setKeys((prev) => [...prev, makeKey()]);
  const removeKey = (index: number) => setKeys((prev) => prev.filter((_, i) => i !== index));

  return { keys, addKey, removeKey };
}

// Owns every piece of derived state the form needs (validation, the server-rejection-to-field
// mapping, the position list's add/remove) so OfferForm itself only maps this to JSX.
export function useOfferFormFields(
  offerFormValues: OfferFormValues,
  onSubmit: (values: OfferFormValues) => void,
  submitError: string,
) {
  const formik = useFormik<OfferFormValues>({
    enableReinitialize: true,
    validationSchema: offerFormSchema,
    initialValues: offerFormValues,
    onSubmit,
  });
  const { keys: positionKeys, addKey, removeKey } = useStablePositionKeys(offerFormValues.positions.length, offerFormValues);

  const rejection = submitError ? deriveFieldRejection(submitError) : null;
  const rejectedPositionIndex = findRejectedPositionIndex(formik.values.positions, rejection);

  const fieldError = (field: TopLevelField) => (
    topLevelFieldError(field, formik.touched, formik.errors, rejection)
  );
  const positionError = (index: number, field: 'episode_uuid' | 'node_name' | 'step_position') => (
    positionFieldError(index, field, formik.touched, formik.errors, rejection, rejectedPositionIndex)
  );

  const addPosition = () => {
    formik.setFieldValue('positions', [...formik.values.positions, { ...emptyOfferPosition }]);
    addKey();
  };
  const removePosition = (index: number) => {
    formik.setFieldValue(
      'positions',
      formik.values.positions.filter((_, positionIndex) => positionIndex !== index),
    );
    removeKey(index);
  };

  const {
    handleSubmit, handleChange, setFieldValue, setValues, values, errors, touched, dirty,
  } = formik;

  const [popupImageFileError, setPopupImageFileError] = useState('');

  // No crop step -- the raw file rides the multipart submit unchanged; the object URL is a local
  // preview only. A file failing validatePopupImageFile is rejected here, before setValues runs.
  const handlePopupImageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { target } = event;
    const file = target.files?.[0];
    if (!file) {
      return;
    }
    const validationError = validatePopupImageFile(file);
    if (validationError) {
      setPopupImageFileError(validationError);
      // Clears the picked path so re-selecting the same, now-corrected file still fires a change.
      target.value = '';
      return;
    }
    setPopupImageFileError('');
    setValues((prevValues) => ({
      ...prevValues,
      popup_image_url: URL.createObjectURL(file),
      newPopupImage: { file, fileName: file.name },
    }));
  };

  return {
    handleSubmit,
    handleChange,
    setFieldValue,
    setValues,
    values,
    errors,
    touched,
    dirty,
    fieldError,
    positionError,
    addPosition,
    removePosition,
    positionKeys,
    popupImageFileError,
    handlePopupImageFileChange,
  };
}
